import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Scaffold } from "../Scaffold";
import LoadingSpinner from "../LoadingSpinner";
import { State } from "../redux/types";
import { useSelector, useDispatch } from "react-redux";
import { ConfirmActionDialog } from "../components/ConfirmActionDialog";
import { VendorUpdateDialog } from "../components/VendorUpdateDialog";
import { useVendorActions } from "../hooks/useVendorActions";
import { vendorRequest } from "../api/api";
import { setAdvanceResult } from "../redux/actions";
import { maskPhone } from "../utils/masking";

const VENDOR_KEY = "olo";

export const Olo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const advanceVendorResult = useSelector(
    (state: State) => state.advanceResult
  );

  // Get customerVendorId from navigation state (passed from AdvanceResult)
  const locationState = location.state as { customerVendorId?: string } | null;

  // Try multiple possible property names for the Olo ID, with navigation state as fallback
  const oloAccountId = advanceVendorResult?.customer?.oloAccountId 
    ?? advanceVendorResult?.customer?.customerVendorId 
    ?? advanceVendorResult?.customer?.id
    ?? advanceVendorResult?.value?.oloAccountId
    ?? advanceVendorResult?.value?.customerVendorId
    ?? advanceVendorResult?.value?.id
    ?? locationState?.customerVendorId
    ?? "";

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleBackToResults = () => {
    navigate("/advanceresult");
  };

  const handleVendorActionSuccess = useCallback(async (actionKey: string) => {
    if (actionKey === "delete") {
      navigate("/advanceresult");
      return;
    }

    if (!oloAccountId) { return; }

    setIsRefreshing(true);
    try {
      await new Promise<void>(resolve => { setTimeout(resolve, 1000); });
      const response = await vendorRequest(oloAccountId, VENDOR_KEY);
      if (response) {
        dispatch(setAdvanceResult({ ...response }));
      }
    } catch (error) {
      console.debug("Olo vendor refetch failed", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [oloAccountId, dispatch, navigate]);

  // ============================================
  // Vendor Actions (UPDATE & DELETE)
  // ============================================

  const {
    dialogOpen: vendorDialogOpen,
    updateDialogOpen: vendorUpdateDialogOpen,
    currentAction: vendorCurrentAction,
    loading: vendorLoading,
    openDialog: openVendorDialog,
    closeDialog: closeVendorDialog,
    closeUpdateDialog: closeVendorUpdateDialog,
    confirmAction: confirmVendorAction,
    confirmUpdate: confirmVendorUpdate,
  } = useVendorActions({
    vendorKey: VENDOR_KEY,
    customerVendorId: oloAccountId,
    vendorName: "Olo",
    onSuccess: handleVendorActionSuccess,
    onFailure: () => {
      // Handle failure if needed
    },
  });

  const buttonStyle = {
    backgroundColor: "#041E42",
    color: "white",
    px: 3,
    py: 1,
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#0a3d7a",
    },
  };

  if (!advanceVendorResult) {
    return (
      <Scaffold navBar center>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <LoadingSpinner />
        </Box>
      </Scaffold>
    );
  }

  return (
    <Scaffold navBar center>
      {isRefreshing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            bgcolor: "rgba(255,255,255,0.7)",
          }}
        >
          <LoadingSpinner />
        </Box>
      )}
      {/* Back Button - Fixed to top left */}
      <Box
        sx={{
          position: "fixed",
          top: 80,
          left: 20,
          zIndex: 10,
          mt: "2rem",
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBackToResults}
          sx={{
            color: "text.secondary",
            textTransform: "none",
            fontSize: "0.875rem",
          }}
        >
          BACK TO RESULTS
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        {/* Olo Title - Centered */}
        <Typography
          variant="h4"
          sx={{ mb: 3, fontWeight: "bold", textAlign: "center", width: "100%" }}
        >
          Olo
        </Typography>

        {/* Customer Account Card */}
        <Card
            sx={{
              border: "3px solid rgba(0,0,0,0.1)",
              borderRadius: 2,
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              minWidth: "400px",
              maxWidth: "600px",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                Customer Account
              </Typography>

              {advanceVendorResult ? (
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Name:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.firstName}{" "}
                      {advanceVendorResult.customer?.lastName}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Email:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.email}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Phone:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {maskPhone(advanceVendorResult.customer?.phoneNumber ?? "")}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Olo Account ID:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.oloAccountId ?? "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Olo Account Status:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.oloAccountStatus ?? "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Olo Account Associated:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.oloAccountAssociated ?? "N/A"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Address:
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {advanceVendorResult.customer?.address ?? "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No customer data available
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  sx={buttonStyle}
                  onClick={() => openVendorDialog("update")}
                >
                  UPDATE
                </Button>

                <Button
                  variant="contained"
                  sx={{ ...buttonStyle, backgroundColor: "#b71c1c", "&:hover": { backgroundColor: "#7f0000" } }}
                  onClick={() => openVendorDialog("delete")}
                >
                  DELETE
                </Button>

              </Box>
            </CardContent>
          </Card>
      </Box>

      {/* Confirmation Dialog for Vendor Actions (DELETE) */}
      <ConfirmActionDialog
        open={vendorDialogOpen}
        actionLabel={vendorCurrentAction?.label ?? ""}
        description={vendorCurrentAction?.description ?? ""}
        confirmText={vendorCurrentAction?.confirmText}
        loading={vendorLoading}
        onConfirm={confirmVendorAction}
        onCancel={closeVendorDialog}
      />

      {/* Vendor Update Dialog with Form */}
      <VendorUpdateDialog
        open={vendorUpdateDialogOpen}
        vendorName="Olo"
        loading={vendorLoading}
        initialValues={{
          firstName: advanceVendorResult?.customer?.firstName,
          lastName: advanceVendorResult?.customer?.lastName,
          email: advanceVendorResult?.customer?.email,
          phoneNumber: advanceVendorResult?.customer?.phoneNumber,
          dateOfBirth: advanceVendorResult?.customer?.birthDate,
        }}
        onConfirm={confirmVendorUpdate}
        onCancel={closeVendorUpdateDialog}
        hideBirthDate
      />
    </Scaffold>
  );
};
